/**
 * Account ID encoding/decoding utilities
 *
 * These functions convert between Instagram usernames/URLs and Firestore-safe account IDs.
 * IMPORTANT: encodeAccountId must match backend's sanitize_account_id() exactly!
 */

/**
 * Encode an Instagram account URL/username to a Firestore-safe account ID.
 * Matches backend's utils/account_utils.py sanitize_account_id logic.
 */
export const encodeAccountId = (accountUrl: string): string => {
    // Extract username from URL
    let url = accountUrl.trim().replace(/\/+$/, '');

    let username: string;
    if (url.includes('instagram.com/')) {
        username = url.split('instagram.com/')[1].split('/')[0].split('?')[0];
    } else if (url.startsWith('@')) {
        username = url.substring(1);
    } else {
        username = url.split('/').pop()?.split('?')[0] || url;
    }

    // Convert to lowercase
    username = username.toLowerCase();

    // Replace special characters with readable tokens
    // Order matters: replace underscore first to avoid conflicts
    let sanitized = username.replace(/_/g, '-underbar-');
    sanitized = sanitized.replace(/\./g, '-dot-');

    // Replace any remaining special chars with hyphen
    sanitized = sanitized.replace(/[^a-z0-9-]/g, '-');

    // Collapse multiple hyphens
    sanitized = sanitized.replace(/-+/g, '-');

    // Remove leading/trailing hyphens
    sanitized = sanitized.replace(/^-+|-+$/g, '');

    // Ensure it starts with a letter
    if (sanitized && !/^[a-z]/.test(sanitized)) {
        sanitized = 'ig-' + sanitized;
    }

    // Truncate to reasonable length
    return sanitized.substring(0, 50) || 'unknown';
};

/**
 * Decode a Firestore account ID back to a display-friendly Instagram username.
 * Reverses the encoding done by encodeAccountId.
 *
 * Example: "john-underbar-doe-dot-smith" -> "john_doe.smith"
 */
export const decodeAccountId = (accountId: string): string => {
    if (!accountId) return '';

    let username = accountId;

    // Remove the "ig-" prefix if present (added for usernames starting with numbers)
    if (username.startsWith('ig-')) {
        username = username.substring(3);
    }

    // Reverse the token replacements (order matters - reverse of encoding)
    // Replace -dot- back to .
    username = username.replace(/-dot-/g, '.');
    // Replace -underbar- back to _
    username = username.replace(/-underbar-/g, '_');

    return username;
};
